import { CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltip } from '@angular/material/tooltip';
import {
  CustomTooltipModule,
  LoaderButtonComponent,
} from '@principle-theorem/ng-shared';
import {
  Organisation,
  ParchmentIntegrationStorage,
  Patient,
} from '@principle-theorem/principle-core';
import {
  Countries,
  IParchmentCreatePatient,
  IParchmentCreatePatientResponse,
  IPatient,
} from '@principle-theorem/principle-core/interfaces';
import {
  filterUndefined,
  getError,
  httpsCallable,
  serialise,
  SerialisedData,
  shareReplayCold,
  snapshot,
  WithRef,
} from '@principle-theorem/shared';
import { combineLatest, noop, Observable } from 'rxjs';
import { concatMap, map, switchMap, take } from 'rxjs/operators';
import { OrganisationService } from '../../../../../lib/organisation.service';
import { CurrentPatientScope } from '../../../../app-state/current-patient';
import { ParchmentMarketingTooltipComponent } from '../parchment-marketing-tooltip/parchment-marketing-tooltip.component';

@Component({
  selector: 'pr-parchment-create-prescription-button',
  imports: [
    CommonModule,
    LoaderButtonComponent,
    MatIcon,
    MatButtonModule,
    MatTooltip,
    CustomTooltipModule,
    ParchmentMarketingTooltipComponent,
    CdkOverlayOrigin,
  ],
  templateUrl: './parchment-create-prescription-button.component.html',
  styleUrl: './parchment-create-prescription-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'flex-1',
  },
})
export class ParchmentCreatePrescriptionButtonComponent {
  isEnabled$: Observable<boolean>;
  patient$: Observable<WithRef<IPatient>>;
  patientLink$: Observable<string | undefined>;
  parchmentHomepageUrl = 'https://www.parchment.health/';
  syncingPatientTooltip = signal(
    'Please wait while we sync the patient with Parchment...'
  );
  isLoading = signal(true);
  validationErrors$: Observable<string | undefined>;

  positions: ConnectedPosition[] = [
    {
      originX: 'center',
      originY: 'bottom',
      overlayX: 'center',
      overlayY: 'top',
    },
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
    },
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
    },
  ];

  constructor(
    organisationService: OrganisationService,
    private _snackbar: MatSnackBar,
    private _currentPatient: CurrentPatientScope
  ) {
    this.isEnabled$ = organisationService.organisation$.pipe(
      filterUndefined(),
      switchMap(async (organisation) => {
        const integration = await new ParchmentIntegrationStorage().get(
          Organisation.integrationCol(organisation)
        );

        return ParchmentIntegrationStorage.isEnabled(integration?.data);
      }),
      shareReplayCold()
    );

    this.patient$ = this._currentPatient.doc$.pipe(
      filterUndefined(),
      shareReplayCold()
    );

    this.validationErrors$ = this.patient$.pipe(
      switchMap((patient) => this.validatePatientDetails(patient)),
      shareReplayCold()
    );

    this.patientLink$ = this.patient$.pipe(
      map((patient) => patient.metadata?.parchment?.url),
      shareReplayCold()
    );

    combineLatest([
      organisationService.organisation$.pipe(filterUndefined()),
      this.patient$,
      this.patientLink$,
      this.isEnabled$,
      this.validationErrors$,
    ])
      .pipe(
        take(1),
        concatMap(
          async ([
            organisation,
            patient,
            patientLink,
            isEnabled,
            validationErrors,
          ]) => {
            if (patientLink || !isEnabled || validationErrors) {
              return;
            }

            try {
              const result = await snapshot(
                httpsCallable<
                  SerialisedData<IParchmentCreatePatient>,
                  IParchmentCreatePatientResponse
                >('http-parchment-createPatient')(
                  serialise({
                    organisationRef: organisation.ref,
                    patientRef: patient.ref,
                  })
                )
              );

              if (result.status === 'success') {
                this._snackbar.open(
                  'Patient created in Parchment successfully.'
                );
                return;
              }

              this._snackbar.open(
                'Failed to create patient in Parchment. Please try again.'
              );
            } catch (error) {
              const message = `An error occurred while creating the patient in Parchment: ${getError(
                error
              )}`;
              this._snackbar.open(message);
              this.syncingPatientTooltip.set(message);
            }

            this.isLoading.set(false);
          }
        ),
        takeUntilDestroyed()
      )
      .subscribe(noop);
  }

  async validatePatientDetails(
    patient: WithRef<IPatient>
  ): Promise<string | undefined> {
    const validationErrors: string[] = [];

    if (!patient.dateOfBirth) {
      validationErrors.push('Date of birth is required');
    }

    if (!patient.email) {
      validationErrors.push('Email is required');
    }

    const phone = await Patient.resolveMobileNumber(patient);
    if (!phone) {
      validationErrors.push('Phone number is required');
    }

    const address = patient.metadata?.address?.components;
    if (address?.country !== Countries.Australia) {
      validationErrors.push('Address is required and must be in Australia');
    }

    if (!validationErrors.length) {
      return;
    }

    return validationErrors.join('; ');
  }
}
